import { z } from "zod";

const nullableUrlSchema = z.url().nullable();
const nullableDateTimeSchema = z.iso.datetime({ offset: true }).nullable();

export const researchProviderSchema = z.enum(["instagram_graph", "demo"]);

export const researchProviderStateSchema = z.enum([
  "connected",
  "setup_required",
  "syncing",
  "degraded",
  "error",
]);

export const researchProviderStatusSchema = z.object({
  provider: researchProviderSchema,
  state: researchProviderStateSchema,
  message: z.string().trim().min(1).max(500),
  lastSyncedAt: nullableDateTimeSchema,
});

export const researchFeedbackSchema = z.enum(["like", "dislike"]);

export const researchFeedbackInputSchema = z.object({
  feedback: researchFeedbackSchema,
});

export const researchFeedbackResponseSchema = z.object({
  reelId: z.string().trim().min(1).max(200),
  feedback: researchFeedbackSchema,
});

export const researchReelMetricsSchema = z.object({
  views: z.number().int().nonnegative().nullable(),
  plays: z.number().int().nonnegative().nullable(),
  likes: z.number().int().nonnegative(),
  comments: z.number().int().nonnegative(),
  shares: z.number().int().nonnegative().nullable(),
  saves: z.number().int().nonnegative().nullable(),
  reach: z.number().int().nonnegative().nullable(),
  accountFollowers: z.number().int().nonnegative().nullable(),
});

export const researchMomentumSchema = z.object({
  score: z.number().int().min(0).max(100),
  label: z.enum(["early", "rising", "breakout", "established"]),
  viewVelocityPerHour: z.number().nonnegative().nullable(),
  engagementRate: z.number().nonnegative().nullable(),
  outlierMultiplier: z.number().nonnegative().nullable(),
});

export const researchCommentSchema = z.object({
  id: z.string().trim().min(1).max(200),
  text: z.string().trim().min(1).max(2_200),
  likeCount: z.number().int().nonnegative().default(0),
});

export const researchDetailedAnalysisSchema = z.object({
  method: z.literal("deterministic_v1"),
  generatedAt: z.iso.datetime({ offset: true }),
  summary: z.string().trim().min(1).max(1_000),
  hook: z.object({
    type: z.enum([
      "question",
      "how_to",
      "listicle",
      "problem",
      "proof",
      "urgency",
      "statement",
    ]),
    evidence: z.string().trim().min(1).max(500),
    whyItWorks: z.string().trim().min(1).max(1_000),
  }),
  format: z.object({
    type: z.enum([
      "tutorial",
      "listicle",
      "transformation",
      "comparison",
      "product_demo",
      "pov",
      "story",
    ]),
    structure: z.array(z.string().trim().min(1).max(300)).min(1).max(8),
    pacing: z.string().trim().min(1).max(500),
  }),
  momentum: z.object({
    score: z.number().int().min(0).max(100),
    label: researchMomentumSchema.shape.label,
    rationale: z.string().trim().min(1).max(1_000),
    signals: z.array(z.string().trim().min(1).max(300)).min(1).max(8),
  }),
  audience: z.object({
    primaryAudience: z.string().trim().min(1).max(300),
    intent: z.enum(["low", "medium", "high"]),
    commentThemes: z
      .array(z.string().trim().min(1).max(120))
      .min(1)
      .max(8),
    questions: z.array(z.string().trim().min(1).max(500)).max(5),
  }),
  remixBrief: z.object({
    angle: z.string().trim().min(1).max(500),
    originalHook: z.string().trim().min(1).max(300),
    beats: z.array(z.string().trim().min(1).max(300)).min(3).max(8),
    captionDirection: z.string().trim().min(1).max(500),
    callToAction: z.string().trim().min(1).max(300),
  }),
});

export const researchAnalysisStatusSchema = z.enum([
  "not_started",
  "ready",
  "failed",
]);

export const researchReelSchema = z.object({
  id: z.string().trim().min(1).max(200),
  provider: researchProviderSchema,
  sourceMediaId: z.string().trim().min(1).max(200),
  permalink: z.url(),
  mediaUrl: nullableUrlSchema,
  thumbnailUrl: nullableUrlSchema,
  username: z.string().trim().min(1).max(100),
  caption: z.string().trim().max(2_200),
  publishedAt: z.iso.datetime({ offset: true }),
  durationSeconds: z.number().nonnegative().max(3_600).nullable(),
  metrics: researchReelMetricsSchema,
  momentum: researchMomentumSchema,
  feedback: researchFeedbackSchema.nullable(),
  analysisStatus: researchAnalysisStatusSchema,
  detailedAnalysis: researchDetailedAnalysisSchema.nullable(),
});

export const researchReelListQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(500).optional(),
  limit: z.coerce.number().int().min(1).max(60).default(30),
  feedback: researchFeedbackSchema.optional(),
});

export const researchReelListResponseSchema = z.object({
  items: z.array(researchReelSchema),
  nextCursor: z.string().nullable(),
  providerStatus: researchProviderStatusSchema,
});

export const researchDetailedAnalysisResponseSchema = z.object({
  reelId: z.string().trim().min(1).max(200),
  analysis: researchDetailedAnalysisSchema,
});

export const researchAnalysisInputSchema = z.object({
  reel: researchReelSchema.pick({
    id: true,
    username: true,
    caption: true,
    publishedAt: true,
    durationSeconds: true,
    metrics: true,
    momentum: true,
  }),
  comments: z.array(researchCommentSchema).max(500).default([]),
});

export type ResearchProvider = z.infer<typeof researchProviderSchema>;
export type ResearchProviderState = z.infer<
  typeof researchProviderStateSchema
>;
export type ResearchProviderStatus = z.infer<
  typeof researchProviderStatusSchema
>;
export type ResearchFeedback = z.infer<typeof researchFeedbackSchema>;
export type ResearchFeedbackInput = z.input<
  typeof researchFeedbackInputSchema
>;
export type ResearchFeedbackResponse = z.infer<
  typeof researchFeedbackResponseSchema
>;
export type ResearchReelMetrics = z.infer<typeof researchReelMetricsSchema>;
export type ResearchMomentum = z.infer<typeof researchMomentumSchema>;
export type ResearchComment = z.infer<typeof researchCommentSchema>;
export type ResearchDetailedAnalysis = z.infer<
  typeof researchDetailedAnalysisSchema
>;
export type ResearchAnalysisStatus = z.infer<
  typeof researchAnalysisStatusSchema
>;
export type ResearchReel = z.infer<typeof researchReelSchema>;
export type ResearchReelListQuery = z.input<
  typeof researchReelListQuerySchema
>;
export type ResearchReelListResponse = z.infer<
  typeof researchReelListResponseSchema
>;
export type ResearchDetailedAnalysisResponse = z.infer<
  typeof researchDetailedAnalysisResponseSchema
>;
export type ResearchAnalysisInput = z.input<typeof researchAnalysisInputSchema>;
